import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReviewTabs from './ReviewTabs';

// Developer Quality leaderboard — scores computed at read time from posted
// review findings, weighted by how OBVIOUS each flagged mistake was.

const GRADE_COLORS = { A: '#1a7f37', B: '#4d8f2f', C: '#b08800', D: '#c4560a', F: '#c62828' };

function isoDaysAgo(days) {
  const d = new Date(Date.now() - days * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

export function GradeChip({ grade }) {
  return (
    <span style={{
      display: 'inline-block', minWidth: 26, textAlign: 'center', fontWeight: 700,
      borderRadius: 4, padding: '2px 8px', color: '#fff',
      background: GRADE_COLORS[grade] || '#888',
    }}>{grade}</span>
  );
}

export default function DeveloperQuality() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [minPrs, setMinPrs] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    axios.get('/code-review/developer-quality', { params: { from, to, min_prs: minPrs } })
      .then((res) => { setData(res.data); setLoading(false); })
      .catch((err) => { setError(err.response?.data?.error || err.message); setLoading(false); });
  }, [from, to, minPrs]);

  useEffect(() => { load(); }, [load]);

  const setWindow = (days) => { setFrom(isoDaysAgo(days)); setTo(isoDaysAgo(0)); };

  return (
    <div>
      <div className="page-header">
        <h2>Developer Quality</h2>
        <p>
          Quality scores from posted AI review comments. Obvious mistakes (basics a developer
          should catch before pushing) cost far more points than subtle, expert-level findings.
          Covers only PRs reviewed with comment posting enabled — a coaching signal, not a verdict.
        </p>
      </div>
      <div className="page-body">
        <ReviewTabs />

        <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {[30, 90, 180].map(d => (
            <button key={d} className="btn btn-default" onClick={() => setWindow(d)}>Last {d}d</button>
          ))}
          <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
          <label>Min PRs <input type="number" min="1" style={{ width: 60 }} value={minPrs}
            onChange={e => setMinPrs(Math.max(1, parseInt(e.target.value || '1', 10)))} /></label>
        </div>

        {loading && <div className="spinner" />}
        {error && <div className="error-msg">{error}</div>}

        {data && !loading && (
          <>
            {data.classified_pct < 100 && (
              <div className="card" style={{ padding: '10px 16px', marginBottom: 16, background: '#fff8e1', border: '1px solid #f0d060' }}>
                Only {data.classified_pct}% of findings in this window are obviousness-classified.
                Unclassified findings count at neutral weight — run the “Daily Finding Classifier”
                job (Jobs page) to backfill the rest.
              </div>
            )}
            <div className="card" style={{ padding: '16px 20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc' }}>
                    <th style={{ padding: 8 }}>Developer</th>
                    <th style={{ padding: 8 }}>Score</th>
                    <th style={{ padding: 8 }}>Grade</th>
                    <th style={{ padding: 8 }}>PRs</th>
                    <th style={{ padding: 8 }}>Findings</th>
                    <th style={{ padding: 8 }} title="Findings with obviousness 4-5">Obvious</th>
                    <th style={{ padding: 8 }} title="Findings with obviousness 1-2">Subtle</th>
                    <th style={{ padding: 8 }} title="Weighted penalty per PR — lower is better">Penalty/PR</th>
                    <th style={{ padding: 8 }} title="Share of flagged issues the developer fixed">Fixed rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.authors.map((a) => (
                    <tr key={a.author_key}
                      style={{ borderBottom: '1px solid #eee', cursor: 'pointer', opacity: a.insufficient_data ? 0.55 : 1 }}
                      onClick={() => navigate(`/code-review/developer-quality/detail?author=${encodeURIComponent(a.author_key)}&from=${from}&to=${to}`)}>
                      <td style={{ padding: 8 }}>
                        {a.author}
                        {a.raw_authors.length > 1 &&
                          <small style={{ color: '#888' }}> (+{a.raw_authors.length - 1} alias)</small>}
                      </td>
                      <td style={{ padding: 8, fontWeight: 700 }}>
                        {a.insufficient_data ? '—' : a.score}
                      </td>
                      <td style={{ padding: 8 }}>
                        {a.insufficient_data
                          ? <small style={{ color: '#888' }}>&lt;{data.min_prs} PRs</small>
                          : <GradeChip grade={a.grade} />}
                      </td>
                      <td style={{ padding: 8 }}>{a.prs}</td>
                      <td style={{ padding: 8 }}>
                        {a.findings_total}
                        {a.unclassified_count > 0 &&
                          <small style={{ color: '#b08800' }}> ({a.unclassified_count} unclassified)</small>}
                      </td>
                      <td style={{ padding: 8, color: a.obvious_count ? '#c62828' : undefined }}>{a.obvious_count}</td>
                      <td style={{ padding: 8 }}>{a.subtle_count}</td>
                      <td style={{ padding: 8 }}>{a.penalty_per_pr}</td>
                      <td style={{ padding: 8 }}>
                        {a.acted_upon_rate === null ? '—' : `${Math.round(a.acted_upon_rate * 100)}%`}
                      </td>
                    </tr>
                  ))}
                  {data.authors.length === 0 && (
                    <tr><td colSpan="9" style={{ padding: 16, color: '#888' }}>
                      No posted reviews in this window.
                    </td></tr>
                  )}
                </tbody>
              </table>
              <p style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
                score = 100 / (1 + penalty_per_pr / {data.scoring.scale}) · penalty = severity ×
                obviousness × confidence, discounted when fixed ({data.scoring.acted_upon_discount}×)
                or the PR was abandoned ({data.scoring.abandoned_pr_weight}×). Click a row for the
                full breakdown.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
